import React, { useState } from "react";
import { Box, Button, Modal } from "@mui/material";
import { Button as Btn, Typography } from "@material-ui/core";
import { Spinner } from "../../../components";
import EditIcon from "../../../assets/admin/edit-icon.svg";
import CloseIcon from "@mui/icons-material/Close";
import ApiService from "../../../service/ApiService";
import { DatabaseEntities } from "../../../model/enum/database-entities.enum";
import AlertService from "../../../service/AlertService";
import ToastMessages from "../../../model/enum/toast-messages.enum";
import Misc from "../../../utils/Misc";
import Tag from "../../../model/tag";
import useStyles from "./styles";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "35rem",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: "8px",
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
  "@media (max-width: 640px)": {
    width: "80%",
  },
};

interface Props {
  tag?: Tag;
  resetList?: () => void;
}

const TagModal = ({ tag, resetList }: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const edit = tag != null;
  const id = edit ? tag?.id : 0;
  const [name, setName] = useState(edit ? tag?.name : "");
  const [loading, setLoading] = useState(false);

  const handleName = (e: any) => setName(e.target.value);

  const handleCreate = async (payload: Tag) => {
    await ApiService.post<Tag>(DatabaseEntities.TAGS, payload)
      .then(() => {
        AlertService.success(ToastMessages.CREATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.CREATE_FAILED);
      });
  };

  const handleUpdate = async (payload: Tag) => {
    await ApiService.put<Tag>(`${DatabaseEntities.TAGS}/${payload.id}`, payload)
      .then(() => {
        AlertService.success(ToastMessages.UPDATED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.UPDATE_FAILED);
      });
  };

  const handleDelete = async (id: number) => {
    await ApiService.delete(`${DatabaseEntities.TAGS}/${id}`)
      .then(() => {
        AlertService.success(ToastMessages.DELETED);
        resetList?.();
      })
      .catch(() => {
        AlertService.error(ToastMessages.DELETE_FAILED);
      });
  };

  const invalidInput = (): boolean => {
    return Misc.isNullOrEmpty(name);
  };

  const handleSubmit = async (e: any) => {
    if (invalidInput()) return;

    setLoading(true);
    const payload: Tag = {
      id: id,
      name: name,
    };

    if (edit) await handleUpdate(payload);
    else
      await handleCreate(payload).then(() => {
        setName("");
      });

    setLoading(false);
    handleClose();
  };

  return (
    <div>
      {edit ? (
        <Button onClick={handleOpen}>
          <img src={EditIcon} alt="edit icon" />
        </Button>
      ) : (
        <Btn
          className={classes.headerButton}
          variant="contained"
          onClick={handleOpen}
        >
          + Add Tag
        </Btn>
      )}
      {open && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          sx={{ overflowY: "auto" }}
        >
          {loading ? (
            <Spinner />
          ) : (
            <Box sx={style}>
              <div className={classes.headerDiv}>
                <Typography className={classes.title}>
                  {edit ? "Edit tag info" : "Add new tag"}
                </Typography>
                <Button onClick={handleClose} sx={{ color: "black" }}>
                  <CloseIcon />
                </Button>
              </div>
              <hr />
              <div className={classes.form}>
                <div className={classes.inputGroup}>
                  <label className={classes.label}>Name</label>
                  <input
                    type="text"
                    className={classes.input}
                    value={name}
                    onChange={handleName}
                    placeholder="Tag"
                  />
                </div>
              </div>
              <div className={classes.buttonDiv}>
                <Btn
                  className={classes.button}
                  onClick={handleSubmit}
                  disabled={invalidInput()}
                >
                  {edit ? "Save" : "Create"}
                </Btn>
              </div>
            </Box>
          )}
        </Modal>
      )}
    </div>
  );
};

export default TagModal;
